import {
  createFileRoute,
  Link,
  Outlet,
  useChildMatches,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { PackageCheck, Plus } from "lucide-react";
import type { GRNListItemDto } from "@procureflow/shared-types";
import {
  TransactionListShell,
  type ColumnDef,
  type FilterConfig,
} from "@/components/transaction-list-shell";
import { fetchWithAuth } from "@/lib/auth-client";
import { formatDate } from "@/lib/format";
import { ExportCsvButton } from "@/components/export-csv-button";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/auth-context";

export const Route = createFileRoute("/grn")({
  head: () => ({
    meta: [
      { title: "Goods Receipt Notes — ProcureFlow" },
      {
        name: "description",
        content: "Review goods receipt notes recorded against purchase orders.",
      },
    ],
  }),
  component: function GRNPageOrOutlet() {
    const child = useChildMatches();
    return child.length > 0 ? <Outlet /> : <GRNListPage />;
  },
});

// GRN has no status lifecycle — no status tabs (transaction-agent-brief §5).
const COLUMNS: ColumnDef<GRNListItemDto>[] = [
  {
    id: "grn_number",
    header: "GRN Number",
    cell: (grn) => (
      <Link to="/grn/$id" params={{ id: grn.id }} className="flex items-center gap-3">
        <div className="h-8 w-8 rounded-lg bg-[var(--success)]/10 grid place-items-center [color:var(--success)] shrink-0">
          <PackageCheck className="h-3.5 w-3.5" />
        </div>
        <span className="font-mono text-xs font-medium hover:text-primary transition-colors">
          {grn.grn_number}
        </span>
      </Link>
    ),
  },
  {
    id: "po_number",
    header: "PO Number",
    // US-065 — a GRN aggregated from several POs lists each distinct source PO
    // number; the single-PO receive path shows the one po_number.
    cell: (grn) => (
      <span className="font-mono text-xs text-muted-foreground">
        {grn.source_po_numbers && grn.source_po_numbers.length > 0
          ? grn.source_po_numbers.join(", ")
          : grn.po_number}
      </span>
    ),
  },
  {
    id: "vendor",
    header: "Vendor",
    cell: (grn) => (
      <span className="font-medium truncate max-w-[220px] block">{grn.vendor_name}</span>
    ),
  },
  {
    id: "date_received",
    header: "Date Received",
    cellClassName: "text-muted-foreground",
    cell: (grn) => formatDate(grn.date_received),
  },
  {
    id: "line_count",
    header: "Lines",
    cellClassName: "tabular-nums text-muted-foreground",
    cell: (grn) => grn.line_count,
  },
  {
    id: "created_by",
    header: "Created By",
    cellClassName: "text-muted-foreground",
    cell: (grn) => grn.created_by_name,
  },
  {
    id: "invoice_status",
    header: "Invoice",
    // US-066 — the GR→invoice conversion is mandatory, so a GR with no invoice
    // is an outstanding action. Surface it with a "Needs invoice" badge.
    cell: (grn) =>
      grn.has_invoice === false ? (
        <span className="inline-flex items-center rounded-full bg-[var(--warning)]/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide [color:var(--warning)]">
          Needs invoice
        </span>
      ) : grn.has_invoice ? (
        <span className="inline-flex items-center rounded-full bg-[var(--success)]/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide [color:var(--success)]">
          Invoiced
        </span>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
  },
];

function GRNListPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // US-065 — Create a GR from one or more POs. The builder POSTs
  // /api/grns/from-pos which is ProcurementOfficer/Admin-only; gate to match.
  const canCreateGr = user?.role === "ProcurementOfficer" || user?.role === "Admin";
  // PO filter options (US-031: GRN list filterable by PO). The shell sends the
  // selected value as `po_id`; the /api/grns handler filters on that exact key.
  const [poOptions, setPoOptions] = useState<{ label: string; value: string }[]>([]);

  useEffect(() => {
    let cancelled = false;
    async function loadPoOptions() {
      try {
        const res = await fetchWithAuth("/api/purchase-orders?page=1&limit=50");
        if (!res.ok) return;
        const { data } = await res.json();
        if (cancelled) return;
        setPoOptions(
          (data as { id: string; po_number: string }[]).map((po) => ({
            label: po.po_number,
            value: po.id,
          })),
        );
      } catch {
        // Filter options are non-critical — the list still works without them.
      }
    }
    loadPoOptions();
    return () => {
      cancelled = true;
    };
  }, []);

  const filters: FilterConfig[] = [
    { id: "po_id", label: "PO", type: "select", options: poOptions },
    // US-066 — narrow to GRs still awaiting an invoice (an outstanding action).
    // Sends ?uninvoiced=true, which the /api/grns handler matches exactly.
    {
      id: "uninvoiced",
      label: "Invoice",
      type: "select",
      options: [{ label: "Needs invoice", value: "true" }],
    },
  ];

  const search = useSearch({ strict: false }) as Record<string, string | undefined>;
  const exportSearchParams = search.po_id
    ? new URLSearchParams({ po_id: search.po_id }).toString()
    : undefined;

  return (
    <TransactionListShell<GRNListItemDto>
      title="Goods Receipt Notes"
      countNoun={{ singular: "receipt", plural: "receipts" }}
      endpoint="/api/grns"
      filters={filters}
      columns={COLUMNS}
      rowKey={(grn) => grn.id}
      rowClassName="cursor-pointer hover:bg-muted/30 transition-colors"
      onRowClick={(grn) => navigate({ to: "/grn/$id", params: { id: grn.id } })}
      emptyIcon={PackageCheck}
      emptyMessage="No goods receipt notes found."
      headerAction={
        <div className="flex items-center gap-2">
          {canCreateGr && (
            <Button asChild data-testid="create-gr-button">
              <Link to="/grn/build">
                <Plus className="h-3.5 w-3.5" /> Create GR
              </Link>
            </Button>
          )}
          <ExportCsvButton
            endpoint="/api/grns/export/csv"
            searchParams={exportSearchParams}
            fallbackFilename="goods-receipts.csv"
          />
        </div>
      }
    />
  );
}
